import { attrGroupLinkedTables, getColDefaultValue } from "components/PimDashboard/data";
import { inputTypes } from "data/globalData";
import { isColorRejected } from "components/PimDashboard/DashboardComponents/Designer/pendingRequiredFields";

const CHOICE_TYPES = new Set([inputTypes.SELECT.key, inputTypes.MULTIPLE_SELECT.key]);

export const isChoiceAttribute = (attr) => CHOICE_TYPES.has(attr?.input_type);

const dependsOn = (attr, key) => attr?.dependency?.[key] === true;

export const toMultiValue = (value) => {
	if (Array.isArray(value)) return value.map((item) => String(item));
	if (value === null || value === undefined || value === "") return [];
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) return [];
		if (trimmed.startsWith("[")) {
			try {
				const parsed = JSON.parse(trimmed);
				if (Array.isArray(parsed)) return parsed.map((item) => String(item));
			} catch {
				return [];
			}
		}
		return [trimmed];
	}
	return [String(value)];
};

const valuesEqual = (left, right, multiple) => {
	if (multiple) {
		const a = toMultiValue(left);
		const b = toMultiValue(right);
		return a.length === b.length && a.every((item, index) => item === b[index]);
	}
	return String(left ?? "") === String(right ?? "");
};

export const allowedOptionValues = (attr, rowByTable, allAttrs) => {
	const values = attr?.attributeValues || [];
	const dependentCols = (allAttrs || []).filter((col) => dependsOn(attr, col.key));
	if (!dependentCols.length) return values.map((row) => row.value);

	return values
		.filter((attrVal) => {
			let allowed = true;
			dependentCols.forEach((col) => {
				const parentVal = rowByTable?.[col.linked_table]?.[col.key];
				const fallback = getColDefaultValue(col);
				if (parentVal && parentVal !== fallback) {
					const matches = attrVal?.dependency?.[col.key]?.includes(parentVal);
					if (!matches) allowed = false;
				}
			});
			return allowed;
		})
		.map((row) => row.value);
};

const nextChoiceValue = (attr, rowByTable, current, allAttrs) => {
	const allowed = new Set(allowedOptionValues(attr, rowByTable, allAttrs));
	const fallback = getColDefaultValue(attr);
	if (attr.input_type === inputTypes.MULTIPLE_SELECT.key) {
		const kept = toMultiValue(current).filter((item) => allowed.has(item));
		if (!kept.length) return Array.isArray(fallback) ? fallback : [];
		return kept;
	}
	const text = current === null || current === undefined ? "" : String(current);
	if (!text || text === String(fallback ?? "") || allowed.has(text)) return text;
	return fallback ?? "";
};

const viewForRow = (data, attr, colorRow) => {
	if (attr.linked_table === attrGroupLinkedTables.color.key) return { ...data, color: colorRow };
	return data;
};

export const reconcileCategorySelects = (data, allAttrs) => {
	const styleKey = attrGroupLinkedTables.style.key;
	const detailsKey = attrGroupLinkedTables.style_details.key;
	const colorKey = attrGroupLinkedTables.color.key;
	const next = {
		...data,
		[styleKey]: { ...(data?.[styleKey] || {}) },
		[detailsKey]: { ...(data?.[detailsKey] || {}) },
		[colorKey]: Array.isArray(data?.[colorKey]) ? data[colorKey].map((row) => ({ ...row })) : [],
	};
	const changed = new Set(["category"]);

	for (let pass = 0; pass < 8; pass += 1) {
		let passChanged = false;
		const targets = (allAttrs || []).filter(
			(attr) => isChoiceAttribute(attr) && attr.key !== "category" && [...changed].some((key) => dependsOn(attr, key)),
		);

		targets.forEach((attr) => {
			if (attr.linked_table === colorKey) {
				next[colorKey] = next[colorKey].map((row) => {
					if (isColorRejected(row)) return row;
					const updated = nextChoiceValue(attr, viewForRow(next, attr, row), row[attr.key], allAttrs);
					if (valuesEqual(row[attr.key], updated, attr.input_type === inputTypes.MULTIPLE_SELECT.key)) return row;
					passChanged = true;
					changed.add(attr.key);
					return { ...row, [attr.key]: updated };
				});
				return;
			}

			const table = next[attr.linked_table];
			if (!table || Array.isArray(table)) return;
			const updated = nextChoiceValue(attr, next, table[attr.key], allAttrs);
			if (valuesEqual(table[attr.key], updated, attr.input_type === inputTypes.MULTIPLE_SELECT.key)) return;
			passChanged = true;
			changed.add(attr.key);
			next[attr.linked_table] = { ...next[attr.linked_table], [attr.key]: updated };
		});

		if (!passChanged) break;
	}

	return next;
};

export const categoryDependentChoices = (allAttrs = []) =>
	allAttrs.filter((attr) => isChoiceAttribute(attr) && attr.key !== "category" && dependsOn(attr, "category"));
